<template>
  <list-page-shell>
    <template #actions>
      <el-button v-permission="['SYSTEM_MENU_CREATE']" type="primary" size="small" @click="openCreateRoot">{{ $t('systemMenusPage.createRoot') }}</el-button>
      <el-button size="small" plain @click="load">{{ $t('common.refresh') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small" class="filter-form">
        <el-form-item :label="$t('common.keyword')">
          <el-input v-model.trim="filter.keyword" :placeholder="$t('systemMenusPage.keywordPh')" clearable />
        </el-form-item>
        <el-form-item :label="$t('grid.type')">
          <el-select v-model="filter.menuTypeCode" clearable :placeholder="$t('common.all')" style="width: 120px;">
            <el-option :value="1" :label="$t('grid.menuTypeMenu')" />
            <el-option :value="2" :label="$t('grid.menuTypeButton')" />
          </el-select>
        </el-form-item>
      </el-form>
    </template>

    <el-table
      v-loading="loading"
      :data="filteredRows"
      size="small"
      border
      row-key="id"
      default-expand-all
      :tree-props="{ children: 'children' }"
    >
      <el-table-column :label="$t('grid.name')" min-width="220">
        <template slot-scope="scope">
          <span>{{ scope.row.menuName }}</span>
          <el-tag size="mini" effect="plain" class="type-tag">{{ scope.row.menuTypeCode === 2 ? $t('grid.menuTypeButton') : $t('grid.menuTypeMenu') }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="routePath" :label="$t('grid.route')" min-width="180" show-overflow-tooltip />
      <el-table-column prop="permissionCode" :label="$t('grid.permissionCode')" min-width="180" show-overflow-tooltip />
      <el-table-column prop="menuGroup" :label="$t('grid.menuGroup')" width="120" />
      <el-table-column prop="iconKey" :label="$t('grid.icon')" width="130" />
      <el-table-column :label="$t('grid.visibility')" width="80" align="center">
        <template slot-scope="scope">{{ scope.row.visibleCode === 2 ? $t('grid.visibleHide') : $t('grid.visibleShow') }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.status')" width="80" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.statusCode === 1 ? 'success' : 'info'">
            {{ scope.row.statusCode === 1 ? $t('common.active') : $t('common.inactive') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sortOrder" :label="$t('grid.sortOrder')" width="70" align="center" />
      <el-table-column :label="$t('common.actions')" width="280" fixed="right">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.menuTypeCode === 1"
            v-permission="['SYSTEM_MENU_CREATE']"
            type="text"
            size="small"
            @click="openCreateChild(scope.row, 1)"
          >{{ $t('systemMenusPage.addChildMenu') }}</el-button>
          <el-button
            v-if="scope.row.menuTypeCode === 1"
            v-permission="['SYSTEM_MENU_CREATE']"
            type="text"
            size="small"
            @click="openCreateChild(scope.row, 2)"
          >{{ $t('systemMenusPage.addChildBtn') }}</el-button>
          <el-button v-permission="['SYSTEM_MENU_EDIT']" type="text" size="small" @click="openEdit(scope.row)">{{ $t('common.edit') }}</el-button>
          <el-button v-permission="['SYSTEM_MENU_DELETE']" type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <form-dialog :title="editingId ? $t('systemMenusPage.editMenu') : $t('systemMenusPage.newMenu')" :visible.sync="editDialogVisible" width="760px">
      <el-form ref="menuFormRef" :model="menuForm" :rules="menuRules" label-width="110px" class="menu-form">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.parentMenu')">
              <el-select v-model="menuForm.parentId" clearable :placeholder="$t('systemMenusPage.topLevel')" style="width: 100%;">
                <el-option :value="null" :label="$t('systemMenusPage.topLevel')" />
                <el-option
                  v-for="item in parentMenuOptions"
                  :key="item.id"
                  :label="item.label"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('grid.type')" prop="menuTypeCode">
              <el-radio-group v-model="menuForm.menuTypeCode">
                <el-radio :label="1">{{ $t('grid.menuTypeMenu') }}</el-radio>
                <el-radio :label="2">{{ $t('grid.menuTypeButton') }}</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('grid.name')" prop="menuName">
              <el-input v-model.trim="menuForm.menuName" maxlength="128" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('grid.permissionCode')">
              <el-input v-model.trim="menuForm.permissionCode" maxlength="128" :placeholder="$t('systemMenusPage.permPh')" />
            </el-form-item>
          </el-col>
        </el-row>

        <template v-if="menuForm.menuTypeCode === 1">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.routePath')">
                <el-input v-model.trim="menuForm.routePath" maxlength="255" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.componentName')">
                <el-input v-model.trim="menuForm.componentName" maxlength="255" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item :label="$t('grid.icon')">
                <el-input v-model.trim="menuForm.iconKey" maxlength="64" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('grid.menuGroup')">
                <el-select v-model="menuForm.menuGroup" clearable style="width: 100%;">
                  <el-option label="home" value="home" />
                  <el-option label="sales" value="sales" />
                  <el-option label="delivery" value="delivery" />
                  <el-option label="finance" value="finance" />
                  <el-option label="reports" value="reports" />
                  <el-option label="system" value="system" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.i18nKeyLabel')">
              <el-input v-model.trim="menuForm.i18nKey" maxlength="128" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.purpose')">
              <el-input v-model.trim="menuForm.menuPurpose" maxlength="32" />
            </el-form-item>
          </el-col>
        </el-row>

        <template v-if="menuForm.menuTypeCode === 1">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.defaultChild')">
                <el-input v-model.trim="menuForm.defaultChildRoute" maxlength="255" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.badgeType')">
                <el-input v-model.trim="menuForm.badgeType" maxlength="32" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.badgeSource')">
                <el-input v-model.trim="menuForm.badgeCountSource" maxlength="64" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('systemMenusPage.externalLink')">
                <el-switch v-model="menuForm.isExternal" />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('grid.sortOrder')">
              <el-input-number v-model="menuForm.sortOrder" :min="0" :step="1" controls-position="right" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.hiddenLabel')">
              <el-switch v-model="menuForm.isHidden" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.displayStatus')">
              <el-radio-group v-model="menuForm.visibleCode">
                <el-radio :label="1">{{ $t('systemMenusPage.showRadio') }}</el-radio>
                <el-radio :label="2">{{ $t('systemMenusPage.hideRadio') }}</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('systemMenusPage.enableStatus')">
              <el-radio-group v-model="menuForm.statusCode">
                <el-radio :label="1">{{ $t('common.active') }}</el-radio>
                <el-radio :label="2">{{ $t('common.inactive') }}</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="saveMenu">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import {
  createSystemMenu,
  deleteSystemMenu,
  getSystemMenuTree,
  updateSystemMenu
} from '@/api/system'

function filterMenuTree(rows, keyword, menuTypeCode) {
  const list = rows || []
  return list.reduce((acc, item) => {
    const children = filterMenuTree(item.children || [], keyword, menuTypeCode)
    const matchedKeyword = !keyword || [item.menuName, item.permissionCode, item.routePath].some(v => String(v || '').toLowerCase().includes(keyword))
    const matchedType = !menuTypeCode || Number(item.menuTypeCode) === Number(menuTypeCode)
    if ((matchedKeyword && matchedType) || children.length) {
      acc.push({
        ...item,
        children
      })
    }
    return acc
  }, [])
}

function flattenMenuOptions(rows, level = 0, result = []) {
  (rows || []).forEach(item => {
    if (item.menuTypeCode === 1) {
      result.push({
        id: item.id,
        label: `${'　'.repeat(level)}${level ? '└ ' : ''}${item.menuName}`
      })
      flattenMenuOptions(item.children || [], level + 1, result)
    }
  })
  return result
}

export default {
  name: 'SystemMenus',
  components: { ListPageShell, FormDialog },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      filter: {
        keyword: '',
        menuTypeCode: null
      },
      editDialogVisible: false,
      editingId: null,
      menuForm: {
        parentId: null,
        menuName: '',
        menuTypeCode: 1,
        routePath: '',
        componentName: '',
        iconKey: '',
        menuGroup: '',
        badgeType: '',
        badgeCountSource: '',
        isHidden: false,
        isExternal: false,
        defaultChildRoute: '',
        i18nKey: '',
        menuPurpose: '',
        permissionCode: '',
        sortOrder: 0,
        visibleCode: 1,
        statusCode: 1
      }
    }
  },
  computed: {
    menuRules() {
      return {
        menuName: [{ required: true, message: this.$t('systemMenusPage.rulesName'), trigger: 'blur' }],
        menuTypeCode: [{ required: true, message: this.$t('systemMenusPage.rulesType'), trigger: 'change' }]
      }
    },
    filteredRows() {
      return filterMenuTree(this.rows, String(this.filter.keyword || '').toLowerCase(), this.filter.menuTypeCode)
    },
    parentMenuOptions() {
      return flattenMenuOptions(this.rows).filter(item => item.id !== this.editingId)
    }
  },
  async created() {
    await this.$store.dispatch('fetchUserInfo')
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const resp = await getSystemMenuTree()
        this.rows = resp.data || []
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.menuForm = {
        parentId: null,
        menuName: '',
        menuTypeCode: 1,
        routePath: '',
        componentName: '',
        iconKey: '',
        menuGroup: '',
        badgeType: '',
        badgeCountSource: '',
        isHidden: false,
        isExternal: false,
        defaultChildRoute: '',
        i18nKey: '',
        menuPurpose: '',
        permissionCode: '',
        sortOrder: 0,
        visibleCode: 1,
        statusCode: 1
      }
      this.$nextTick(() => {
        if (this.$refs.menuFormRef) this.$refs.menuFormRef.clearValidate()
      })
    },
    openCreateRoot() {
      this.editingId = null
      this.resetForm()
      this.editDialogVisible = true
    },
    openCreateChild(row, type) {
      this.editingId = null
      this.resetForm()
      this.menuForm.parentId = row.id
      this.menuForm.menuTypeCode = type
      this.editDialogVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.menuForm = {
        parentId: row.parentId || null,
        menuName: row.menuName || '',
        menuTypeCode: row.menuTypeCode || 1,
        routePath: row.routePath || '',
        componentName: row.componentName || '',
        iconKey: row.iconKey || '',
        menuGroup: row.menuGroup || '',
        badgeType: row.badgeType || '',
        badgeCountSource: row.badgeCountSource || '',
        isHidden: !!row.isHidden,
        isExternal: !!row.isExternal,
        defaultChildRoute: row.defaultChildRoute || '',
        i18nKey: row.i18nKey || '',
        menuPurpose: row.menuPurpose || '',
        permissionCode: row.permissionCode || '',
        sortOrder: row.sortOrder != null ? row.sortOrder : 0,
        visibleCode: row.visibleCode || 1,
        statusCode: row.statusCode || 1
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.menuFormRef) this.$refs.menuFormRef.clearValidate()
      })
    },
    saveMenu() {
      this.$refs.menuFormRef.validate(async valid => {
        if (!valid) return
        if (this.menuForm.menuTypeCode === 2 && !this.menuForm.permissionCode) {
          this.$message.warning(this.$t('systemMenusPage.btnNeedPerm'))
          return
        }
        this.saving = true
        try {
          const payload = { ...this.menuForm }
          if (this.editingId) {
            await updateSystemMenu(this.editingId, payload)
          } else {
            await createSystemMenu(payload)
          }
          this.$message.success(this.editingId ? this.$t('systemMenusPage.updated') : this.$t('systemMenusPage.created'))
          this.editDialogVisible = false
          await this.$store.dispatch('fetchUserInfo')
          await this.load()
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('systemMenusPage.saveFailed'))
        } finally {
          this.saving = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('systemMenusPage.confirmDelete', { name: row.menuName }), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await deleteSystemMenu(row.id)
          this.$message.success(this.$t('systemMenusPage.deleted'))
          await this.$store.dispatch('fetchUserInfo')
          await this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.filter-form {
  display: flex;
  flex-wrap: wrap;
}

.type-tag {
  margin-left: 8px;
}

.danger-text {
  color: #f56c6c;
}

.menu-form :deep(.el-input-number .el-input__inner) {
  text-align: left;
}
</style>
